import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { agentsService } from '../services/agentsService';
import { agentEventEmitter } from '../services/agentEventEmitter';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { MoreHorizontal, Plus, Search, User, Check, Copy, Trash2, Play, MessageCircle } from 'lucide-react';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const navigate = useNavigate();

  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const data = await agentsService.getAgents();
      
      const agentsArray = data.agents || [];
      if (!Array.isArray(agentsArray)) {
        throw new Error('Agents data is not an array');
      }
      
      const enhancedData = agentsArray.map(agent => {
        return {
          ...agent,
          lastActive: ['Just now', '2 hours ago', '1 day ago', '3 days ago'][Math.floor(Math.random() * 4)],
          createdAt: agent.created_at_unix_secs ? new Date(agent.created_at_unix_secs * 1000).toLocaleDateString() : 'Unknown date'
        };
      });
      
      setAgents(enhancedData);
    } catch (err) {
      setError(`Failed to load agents: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();

    // Set up listener for agent refresh events
    const unsubscribe = agentEventEmitter.onRefresh(() => {
      fetchAgents();
    });

    return () => unsubscribe();
  }, []);

  const handleDeleteAgent = (agent) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteAgent = async () => {
    if (agentToDelete) {
      try {
        // Call the delete API
        await agentsService.deleteAgent(agentToDelete.agent_id);

        // Show success message with checkmark
        toast.success(
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            <span>Agent "{agentToDelete.name}" deleted successfully</span>
          </div>
        );

        // Close dialog
        setDeleteDialogOpen(false);
        setAgentToDelete(null);

        // Refresh the agents list
        await fetchAgents();
      } catch (error) {
        toast.error(`Failed to delete agent: ${error.message}`);
      }
    }
  };

  const handleCopyAgentId = (agent) => {
    navigator.clipboard.writeText(agent.agent_id);
    toast.success(
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4" />
        <span>Agent ID copied to clipboard</span>
      </div>
    );
  };

  const handleDuplicateAgent = async (agent) => {
    if (isDuplicating) return;
    
    setIsDuplicating(true);
    try {
      // Call the duplicate API with new name
      const newName = `${agent.name} (Copy)`;
      
      await agentsService.duplicateAgent(agent.agent_id, {
        name: newName
      });
      

      // Show success message
      toast.success(
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4" />
          <span>Agent "{agent.name}" duplicated successfully</span>
        </div>
      );

      // Refresh the agents list
      await fetchAgents();
    } catch (error) {
      toast.error(`Failed to duplicate agent: ${error.message}`);
    } finally {
      setIsDuplicating(false);
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (error) {
    return (
      <div className="min-h-screen bg-muted/50 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-card-foreground mb-6">
            AI Agents
          </h1>
          <div className="bg-destructive/10 text-destructive p-6 rounded-lg border border-destructive/20">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h2 className="text-lg font-semibold">Error loading agents</h2>
            </div>
            <p className="mb-4">{error}</p>
            <Button onClick={fetchAgents}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/50 flex flex-col">

      <div className="flex-1 px-8 py-8 space-y-6">

        {/* Action buttons above search */}
        <div className="flex justify-between items-center">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex gap-2 ml-4">
            <Button 
              onClick={() => navigate('/playground')} 
              size="sm" 
              variant="outline"
              className="border-input text-card-foreground hover:bg-muted hover:text-card-foreground rounded-md"
            >
              <Play className="w-3.5 h-3.5 mr-1.5" />
              Playground
            </Button>
            <Button 
              onClick={() => navigate('/agents/new')} 
              size="sm"
              className="rounded-md"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Create Agent
            </Button>
          </div>
        </div>

        {/* Agents Table */}
        <Card className="border border-border bg-card shadow-none">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border">
                  <TableHead className="text-muted-foreground font-semibold">Agent Name</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Status</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Skeleton loading rows
                  Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Skeleton className="h-10 w-10" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-[120px]" />
                            <Skeleton className="h-3 w-[80px]" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-[60px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-8 rounded" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredAgents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                          <User className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-xl font-bold text-card-foreground mb-2">No agents found</h3>
                        <p className="text-muted-foreground mb-4">
                          {searchTerm ? 'No agents match your search criteria.' : 'Get started by creating your first AI agent.'}
                        </p>
                        {!searchTerm && (
                          <Button onClick={() => navigate('/agents/new')}>
                            <Plus className="w-4 h-4 mr-2" />
                            Create Agent
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAgents.map((agent) => (
                    <TableRow
                      key={agent.agent_id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors border-b border-border"
                      onClick={(e) => {
                        if (e.target.closest('.agent-menu')) return;
                        navigate(`/agents/${agent.agent_id}`);
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-accent text-accent-foreground font-semibold">
                              {agent.name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-semibold text-card-foreground">{agent.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {agent.created_at_unix_secs
                                ? new Date(agent.created_at_unix_secs * 1000).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })
                                : 'Unknown date'
                              }
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-success-background text-success-foreground">
                          Active
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="agent-menu h-8 w-8 rounded-none border-0 hover:border-0 focus:border-0 active:border-0"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/conversations?agent=${agent.agent_id}`);
                              }}
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Conversations
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyAgentId(agent);
                              }}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Agent ID
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateAgent(agent);
                              }}
                              disabled={isDuplicating}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate Agent
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAgent(agent);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Agent
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Agent</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <span className="font-semibold">"{agentToDelete?.name}"</span>?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Alert className="mt-4 bg-muted/50 border border-border">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="var(--destructive)">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <AlertTitle className="text-sm font-medium text-destructive">Warning</AlertTitle>
              <AlertDescription className="text-xs leading-relaxed text-destructive">
                This action cannot be undone. This will permanently delete the agent and all associated data.
              </AlertDescription>
            </Alert>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/80"
                onClick={confirmDeleteAgent}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default Agents;
